/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { ISwatchItem, SwatchComponent } from '@msdyn365-commerce/components';
import { IAny, IComponent, IComponentProps, ICoreContext, IGeneric, msdyn365Commerce } from '@msdyn365-commerce/core';
import { DimensionTypes, StringExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { Button, Collapse, format, ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import React, { useCallback } from 'react';

import { RefinerType } from '@msdyn365-commerce/retail-proxy';
import { findMatchingRefinementCriterion, IRefineItemData, IRefineItemToggleNotification, IRefineSubMenuData, isRangeType } from './common';
import { RangeRefineInputComponent } from './range-refine-input-item.component';
import { RangeRefineSliderComponent } from './range-refine-slider-item.component';
import { RefineRatingItemComponent } from './refine-rating-item.component';
import { getRangeType } from './utilities';
import { RefinerItemComponent } from './refiner-item.component';

/**
 * IRefineSubmenuComponent Interface.
 */
export interface IRefineSubmenuComponent extends IComponent<IRefineSubmenuProps> {}

/**
 * IRefineSubmenuData interface.
 */
export interface IRefineSubmenuData {
    refinerSubMenuData: IRefineSubMenuData;
    selectedRefinerValues: IRefineItemData[];
}

/**
 * Refine submenu resources.
 */
export interface IRefineSubmenuResources {
    minValueSliderThumbAriaLabel?: string;
    maxValueSliderThumbAriaLabel?: string;
    minInputAriaLabel?: string;
    maxInputAriaLabel?: string;
    refineSubMenuAriaLabel?: string;
    refineItemAriaLabel?: string;
    refineItemsAriaLabel?: string;
}

/**
 * Properties associated with the RefineSubmenu component.
 */
export interface IRefineSubmenuProps extends IComponentProps<IRefineSubmenuData> {
    subMenuClassName?: string;
    isMobileView?: boolean;
    isDisabled: boolean;
    isExpandedOnInitialLoad: boolean;
    expandSubMenuTimeout?: number;
    telemetryContent?: ITelemetryContent;
    context: ICoreContext<IGeneric<IAny>>;
    getRefineItemAriaLabel?(refinerItemData: IRefineItemData, subMenuList: IRefineSubMenuData): string | undefined;
    onUpdateRefiners(notification: IRefineItemToggleNotification): void;
    shouldDisplaySwatchComponent?(refineItem: IRefineItemData, dimension: DimensionTypes): boolean;
    formatSliderThumb?(sliderValue: string, currencyCode?: string): string;
    formatSliderLabel?(sliderValue: string, currencyCode?: string): string;
    urlBuilder(refiner: IRefineItemToggleNotification): string;
}

// eslint-disable-next-line @typescript-eslint/naming-convention -- This is the standard rule for naming a constant.
const DEFAULT_SUBMENU_EXPAND_TIMEOUT = 350;
// eslint-disable-next-line @typescript-eslint/naming-convention -- This is the standard rule for naming a constant.
const RATING_COMPONENT_DATATYPE = 4;

/**
 * Refine Submenu.
 * @param props - IRefineSubmenuProps.
 * @returns - Render refineSubmenu.
 */
const RefineSubMenuFunctionalComponent: React.FC<IRefineSubmenuProps> = (props: IRefineSubmenuProps) => {
    const {
        data: { refinerSubMenuData, selectedRefinerValues },
        isDisabled,
        subMenuClassName,
        onUpdateRefiners,
        context,
        expandSubMenuTimeout,
        id: moduleId,
        typeName,
        shouldDisplaySwatchComponent,
        isExpandedOnInitialLoad,
        urlBuilder,
        isMobileView,
        telemetryContent
    } = props;

    const {
        minValueSliderThumbAriaLabel,
        maxValueSliderThumbAriaLabel,
        minInputAriaLabel,
        maxInputAriaLabel,
        refineSubMenuAriaLabel,
        refineItemAriaLabel,
        refineItemsAriaLabel
    } = props.moduleResources as IRefineSubmenuResources;

    const [shouldRefinerExpand, setShouldRefinerExpand] = React.useState(isExpandedOnInitialLoad);
    const [isRefinerExpanded, setIsRefinerExpanded] = React.useState(false);

    const onEntering = useCallback(() => {
        setIsRefinerExpanded(!isRefinerExpanded);
    }, [isRefinerExpanded]);

    const onSliderUpdate = React.useCallback(
        (selectedRefinerItem: IRefineItemData) => (updatedMin: number, updatedMax: number) => {
            onUpdateRefiners({
                refinerSubMenuData,
                refinerItemData: selectedRefinerItem,
                isSelecting: true,
                rangeStart: updatedMin,
                rangeEnd: updatedMax
            });
        },
        [onUpdateRefiners, refinerSubMenuData]
    );

    const onClickRefineItem = React.useCallback(
        (selectedRefinerItem: IRefineItemData) => () => {
            onUpdateRefiners({
                refinerSubMenuData,
                refinerItemData: selectedRefinerItem,
                isSelecting: !selectedRefinerItem.isChecked
            });
        },
        [onUpdateRefiners, refinerSubMenuData]
    );

    const onClickRating = React.useCallback(
        (selectedRefinerItem: IRefineItemData) => () => {
            onUpdateRefiners({
                refinerSubMenuData,
                refinerItemData: selectedRefinerItem,
                isSelecting: true
            });
        },
        [onUpdateRefiners, refinerSubMenuData]
    );

    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- Reading config.
    if (refinerSubMenuData.sourceValue === RATING_COMPONENT_DATATYPE && props.context.app.config.hideRating) {
        return null;
    }

    const isSingleSelect = refinerSubMenuData.refinerTypeValue === 0 && refinerSubMenuData.sourceValue === RATING_COMPONENT_DATATYPE;
    const role = isSingleSelect ? { role: 'listbox' } : undefined;

    /**
     * It calls on toggling the submenu.
     */
    const onToggleSubmenu = React.useCallback(() => {
        setShouldRefinerExpand(!shouldRefinerExpand);
    }, [shouldRefinerExpand]);

    /**
     * Refiner item aria label.
     * @param refinerItem - IRefineItemData.
     * @param refinerSubMenuItem - IRefineSubMenuData.
     * @returns Aria label for refine item accordingly.
     */
    const getRefineItemAriaLabel = (refinerItem: IRefineItemData, refinerSubMenuItem: IRefineSubMenuData): string => {
        const productText = refinerItem.name || '';
        const productCount = refinerItem.count ?? 0;
        let refineMenuItemAriaLabel;
        if (productCount === 1) {
            refineMenuItemAriaLabel = refineItemAriaLabel ? format(refineItemAriaLabel, refinerSubMenuItem.name, productText) : '';
        } else {
            refineMenuItemAriaLabel = refineItemsAriaLabel
                ? format(refineItemsAriaLabel, refinerSubMenuItem.name, productText, productCount)
                : '';
        }
        return refineMenuItemAriaLabel;
    };

    /**
     * It decides to show the swatch component or not.
     * @param selectedRefinerItem - Contains the selectedRefineItem details.
     * @param dimensionType - Dimension type.
     * @returns - Returns Swatch component or undefined.
     */
    function showSwatchComponent(selectedRefinerItem: IRefineItemData, dimensionType: DimensionTypes) {
        const swatchItems: ISwatchItem[] = [];
        const shouldShowSwatch = shouldDisplaySwatchComponent?.(selectedRefinerItem, dimensionType);
        if (!shouldShowSwatch) {
            return undefined;
        }
        const text = selectedRefinerItem.name;
        swatchItems.push({
            itemId: `${selectedRefinerItem.refinerItemId}-${dimensionType}-${text}`,
            value: text,
            dimensionType,
            colorHexCode: selectedRefinerItem.swatchColorHexCode,
            imageUrl: selectedRefinerItem.swatchImageUrl
        });
        return (
            <>
                <SwatchComponent
                    className='ms-refine-submenu-item__swatch'
                    apiSettings={context.request.apiSettings}
                    list={swatchItems}
                    isSelectionEnabled={false}
                    isRefineItem
                />
            </>
        );
    }

    /**
     * Gets refiner URL.
     * @param refinerItem - RefineItem.
     * @returns - URL.
     */
    function getRefinerUrl(refinerItem: IRefineItemData): string {
        return urlBuilder({
            refinerSubMenuData,
            refinerItemData: refinerItem,
            isSelecting: true
        });
    }

    /**
     * It renders refine item component.
     * @param subMenuList - Contains the submenu list details.
     * @returns - Refine item component.
     */
    function renderRefineItem(subMenuList: IRefineSubMenuData) {
        return subMenuList.values.map((refinerItem, index) => {
            const dimensionType = (refinerSubMenuData.keyName ?? '') as DimensionTypes;
            const isChecked: boolean = !!findMatchingRefinementCriterion(refinerItem, selectedRefinerValues);
            refinerItem.isChecked = isChecked;
            const isSingleSelectValue = refinerSubMenuData.refinerTypeValue === RefinerType.SingleSelect;
            const label = `${refinerItem.name} (${refinerItem.count ? refinerItem.count : ''})`;
            const refinerUrl = getRefinerUrl(refinerItem);
            return (
                <li className='ms-refine-submenu-item' id={`${refinerItem.name}_${index}`} key={refinerItem.refinerItemId}>
                    <RefinerItemComponent
                        onClick={onClickRefineItem(refinerItem)}
                        isChecked={isChecked}
                        label={label}
                        isSingleSelect={isSingleSelectValue}
                        isDisabled={isDisabled}
                        refineItemAriaLabel={getRefineItemAriaLabel(refinerItem, subMenuList)}
                        id={moduleId}
                        typeName={typeName}
                        data={{}}
                        context={context}
                        refineItemUrl={refinerUrl}
                        telemetryContent={telemetryContent}
                    >
                        {showSwatchComponent(refinerItem, dimensionType)}
                    </RefinerItemComponent>
                </li>
            );
        });
    }

    /**
     * It renders Rating component.
     * @param subMenuList - Contains the submenu list details.
     * @returns - Rating component.
     */
    function renderRatingRefineItem(subMenuList: IRefineSubMenuData) {
        return subMenuList.values.map((refinerItem, index) => {
            const noOfStars = Number.parseInt(refinerItem.name || '0', 10);
            const refinerUrl = getRefinerUrl(refinerItem);
            const refinerRating = Number.parseInt(refinerItem.name, 10);
            const ratingRefiner = selectedRefinerValues.find(value => value.dataTypeValue === refinerItem.dataTypeValue);
            // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- Replace with actual value for empty string.
            const selectedRating = Number.parseInt(ratingRefiner?.name || '0', 10);
            let ratingComponentClass: string = 'ms-rating-component';
            if (refinerRating > selectedRating) {
                ratingComponentClass = `${ratingComponentClass}__up`;
            } else if (refinerRating < selectedRating) {
                ratingComponentClass = `${ratingComponentClass}__down`;
            } else {
                ratingComponentClass = `${ratingComponentClass}__current`;
            }
            return (
                <li
                    className='ms-refine-submenu-item'
                    id={`${refinerItem.name}_${index}`}
                    key={refinerItem.refinerItemId}
                    role='presentation'
                >
                    <RefineRatingItemComponent
                        className={ratingComponentClass}
                        onClick={onClickRating(refinerItem)}
                        avgRatingCount={noOfStars}
                        ratingCountText={refinerItem.label}
                        ratingAriaLabel={`Rating_${refinerItem.label}`}
                        id={moduleId}
                        typeName={typeName}
                        data={{}}
                        context={context}
                        refineItemUrl={refinerUrl}
                        refineRatingName={refinerItem.name}
                        productCount={refinerItem.count?.toString()}
                        isMobileView={isMobileView}
                        telemetryContent={telemetryContent}
                    />
                </li>
            );
        });
    }

    /**
     * It renders range slider or range input based on the rangeType == "input" || rangeType == "slider".
     * @param subMenuList - Contains the submenu list details.
     * @returns - RangeSlider or RangeInput component.
     */
    function renderRangeRefineItem(subMenuList: IRefineSubMenuData): JSX.Element[] | null {
        const rangeType = getRangeType(subMenuList);
        return subMenuList.values.map((refineItem, index) => {
            const selectedRefinementCriterion = findMatchingRefinementCriterion(refineItem, selectedRefinerValues);
            const minValue = refineItem.minValue ?? '';
            const maxValue = refineItem.maxValue ?? '';
            const selectedMinValue = StringExtensions.isNullOrEmpty(selectedRefinementCriterion?.minValue)
                ? undefined
                : Number(selectedRefinementCriterion?.minValue);
            const selectedMaxValue = StringExtensions.isNullOrEmpty(selectedRefinementCriterion?.maxValue)
                ? undefined
                : Number(selectedRefinementCriterion?.maxValue);
            const sliderId = `slider_${String(subMenuList.refinerSubMenuId)}_${String(refineItem.refinerItemId)}`;
            const sliderKey = selectedRefinementCriterion
                ? `${selectedRefinementCriterion.minValue ?? ''}
            -${selectedRefinementCriterion.maxValue ?? ''}`
                : `not-selected-${index}`;
            return (
                <li className='ms-refine-submenu__item list-group-item refine-submenu__item--range' key={refineItem.refinerItemId}>
                    {rangeType === 'input' && (
                        <RangeRefineInputComponent
                            minRangeValue={minValue}
                            maxRangeValue={maxValue}
                            data={{ refinerItemData: refineItem }}
                            id={moduleId}
                            typeName={typeName}
                            context={context}
                            minInputAriaLabel={minInputAriaLabel}
                            maxInputAriaLabel={maxInputAriaLabel}
                        />
                    )}
                    {rangeType === 'slider' && (
                        <RangeRefineSliderComponent
                            key={sliderKey}
                            sliderKey={sliderKey}
                            sliderRangeMinVal={Number(minValue)}
                            sliderRangeMaxVal={Number(maxValue)}
                            selectedSliderMinVal={selectedMinValue}
                            selectedSliderMaxVal={selectedMaxValue}
                            formatLabelString={props.formatSliderLabel}
                            formatTooltipValue={props.formatSliderThumb}
                            minValueSliderThumbAriaLabel={minValueSliderThumbAriaLabel}
                            maxValueSliderThumbAriaLabel={maxValueSliderThumbAriaLabel}
                            isRefinerExpanded
                            sliderId={sliderId}
                            onRangeChange={onSliderUpdate(refineItem)}
                            data={{ refinerItemData: refineItem }}
                            isDisabled={isDisabled}
                            id={moduleId}
                            typeName={typeName}
                            context={context}
                        />
                    )}
                </li>
            );
        });
    }

    /**
     * It renders range type or refine item or rating component based on condition.
     * @param refineSubMenuData - Contains the submenu list details.
     * @returns - RefinerRating or RefinerRange or RefineItem component.
     */
    function renderCollapsibleItems(refineSubMenuData: IRefineSubMenuData) {
        if (isRangeType(refineSubMenuData.dataTypeValue)) {
            return renderRangeRefineItem(refineSubMenuData);
        } else if (refineSubMenuData.sourceValue === RATING_COMPONENT_DATATYPE) {
            return renderRatingRefineItem(refineSubMenuData);
        }
        return renderRefineItem(refineSubMenuData);
    }

    return (
        <div className={`ms-refine-submenu list-group ${subMenuClassName ?? refinerSubMenuData.name ?? ''}`} key={refinerSubMenuData.name}>
            <Button
                className={shouldRefinerExpand ? 'ms-refine-submenu__toggle_expanded' : 'ms-refine-submenu__toggle_collapsed'}
                aria-label={refineSubMenuAriaLabel ?? refinerSubMenuData.name}
                onClick={onToggleSubmenu}
                aria-expanded={shouldRefinerExpand}
            >
                {refinerSubMenuData.name}
            </Button>
            <Collapse isOpen={shouldRefinerExpand} onEntering={onEntering} timeout={expandSubMenuTimeout ?? DEFAULT_SUBMENU_EXPAND_TIMEOUT}>
                <ul className={`ms-refine-submenu__list ${refinerSubMenuData.name ?? ''}`} {...role} aria-label={refinerSubMenuData.name}>
                    {renderCollapsibleItems(refinerSubMenuData)}
                </ul>
            </Collapse>
        </div>
    );
};

/**
 * Refine Summary Component.
 */
export const RefineSubMenuComponent: React.FunctionComponent<IRefineSubmenuProps> = msdyn365Commerce.createComponent<
    // @ts-expect-error -- Required for this signature.
    IRefineSubmenuComponent
>('Refine-SubMenu', {
    component: RefineSubMenuFunctionalComponent
});
